import { supabase } from '../../../shared/lib/supabase';
import type { Project } from '../../../shared/lib/supabase';
import { getCachedProject, setCachedProject, invalidateProjectCache } from './projectCache';

export type { Project };

const getCurrentUserId = async (): Promise<string> => {
  const { data: { user } } = await supabase.auth.getUser();
  
  if (!user) {
    throw new Error('User not authenticated');
  }
  
  return user.id;
};

// Helper function to enhance projects with owner information
const enhanceProjectsWithOwners = async (projects: Project[]): Promise<Project[]> => {
  // Get unique user IDs from projects
  const userIds = [...new Set(projects.map(p => p.user_id).filter(Boolean))];
  
  if (userIds.length === 0) {
    return projects;
  }

  // Fetch user profiles for all owners
  const { data: profiles, error } = await supabase
    .from('user_profiles')
    .select('user_id, first_name, last_name, email, display_name')
    .in('user_id', userIds);

  if (error) {
    console.error('Error fetching user profiles:', error);
    return projects; // Return projects without owner info if fetch fails
  }

  // Create a map for quick lookup
  const profileMap = new Map(profiles?.map(p => [p.user_id, p]) || []);

  // Enhance projects with owner information
  return projects.map(project => ({
    ...project,
    owner: profileMap.get(project.user_id) || null
  }));
};

// List all projects for the current team
export const listProjects = async (teamId?: string): Promise<Project[]> => {
  // If no teamId provided, we'll need to fetch from the user's teams
  if (!teamId) {
    const { data: teamMembers, error: teamError } = await supabase
      .from('team_members')
      .select('team_id')
      .eq('user_id', await getCurrentUserId());
    
    if (teamError) {
      console.error('Error fetching user teams:', teamError);
      throw new Error(`Failed to fetch user teams: ${teamError.message}`);
    }
    
    if (!teamMembers || teamMembers.length === 0) {
      return [];
    }
    
    // Fetch projects from all user's teams
    const teamIds = teamMembers.map(tm => tm.team_id);
    const { data: projects, error } = await supabase
      .from('projects')
      .select('*')
      .in('team_id', teamIds)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching projects:', error);
      throw new Error(`Failed to fetch projects: ${error.message}`);
    }

    // Enhance projects with owner information
    if (projects && projects.length > 0) {
      return await enhanceProjectsWithOwners(projects);
    }
    
    return projects || [];
  }
  
  // Fetch projects for specific team
  const { data: projects, error } = await supabase
    .from('projects')
    .select('*')
    .eq('team_id', teamId)
    .order('created_at', { ascending: false });

  if (error) {
    console.error('Error fetching projects:', error);
    throw new Error(`Failed to fetch projects: ${error.message}`);
  }

  // Enhance projects with owner information
  if (projects && projects.length > 0) {
    return await enhanceProjectsWithOwners(projects);
  }

  return projects || [];
};

// Get a single project by ID with caching
export const getProject = async (projectId: string): Promise<Project> => {
  // Check cache first
  const cachedProject = getCachedProject(projectId);
  if (cachedProject) {
    return cachedProject;
  }

  // Fetch from database
  const { data: project, error } = await supabase
    .from('projects')
    .select('*')
    .eq('id', projectId)
    .single();

  if (error) {
    console.error('Error fetching project:', error);
    throw new Error(`Failed to fetch project: ${error.message}`);
  }

  // Cache the result
  setCachedProject(projectId, project);

  return project;
};

// Create a new project
export const createProject = async ({
  name,
  description,
  workflow_type,
  team_id
}: {
  name: string;
  description?: string;
  workflow_type: 'enzyme' | 'antibody' | 'peptide';
  team_id: string;
}): Promise<string> => {
  const userId = await getCurrentUserId();
  
  const { data, error } = await supabase
    .from('projects')
    .insert({
      user_id: userId,
      team_id,
      name,
      description,
      workflow_type
    })
    .select()
    .single();

  if (error) {
    console.error('Error creating project:', error);
    throw new Error(`Failed to create project: ${error.message}`);
  }

  return data.id;
};

// Update a project
export const updateProject = async (
  projectId: string,
  updates: {
    name?: string;
    description?: string;
    starred?: boolean;
  }
): Promise<void> => {
  const { error } = await supabase
    .from('projects')
    .update({
      ...updates,
      updated_at: new Date().toISOString()
    })
    .eq('id', projectId);

  if (error) {
    console.error('Error updating project:', error);
    throw new Error(`Failed to update project: ${error.message}`);
  }

  // Invalidate cache after update
  invalidateProjectCache(projectId);
};

// Toggle starred status of a project
export const toggleProjectStar = async (projectId: string, starred: boolean): Promise<void> => {
  const { error } = await supabase
    .from('projects')
    .update({
      starred,
      updated_at: new Date().toISOString()
    })
    .eq('id', projectId);

  if (error) {
    console.error('Error toggling project star:', error);
    throw new Error(`Failed to update project star status: ${error.message}`);
  }

  // Invalidate cache after update
  invalidateProjectCache(projectId);
};

// Delete a project
export const deleteProject = async (projectId: string): Promise<void> => {
  const { error } = await supabase
    .from('projects')
    .delete()
    .eq('id', projectId);

  if (error) {
    console.error('Error deleting project:', error);
    throw new Error(`Failed to delete project: ${error.message}`);
  }

  // Invalidate cache after deletion
  invalidateProjectCache(projectId);
};

// Get project statistics (job counts, entries, etc.)
export const getProjectStats = async (projectId: string): Promise<{
  totalJobs: number;
  completedJobs: number;
  runningJobs: number;
  failedJobs: number;
  totalEntries: number;
  totalDataEntries: number;
  maxGeneration: number;
  hasBasePdb: boolean;
  hasBaseCif: boolean;
}> => {
  // Fetch all stats in parallel - now using odyssey_jobs instead of jobs
  const [odysseyJobsResult, entriesResult, dataEntriesResult, baseFilesResult] = await Promise.all([
    supabase
      .from('odyssey_jobs')
      .select('*')
      .eq('project_id', projectId),
    supabase
      .from('entries')
      .select('id')
      .eq('project_id', projectId),
    supabase
      .from('data_entries')
      .select('id')
      .eq('project_id', projectId),
    supabase
      .from('molecule_viewer')
      .select('id, file_type')
      .eq('project_id', projectId)
      .eq('isbasepdb', true)
      .in('file_type', ['pdb', 'cif'])
      .limit(10) // Allow multiple base files but limit for performance
  ]);

  if (odysseyJobsResult.error) {
    console.error('Error fetching project odyssey job stats:', odysseyJobsResult.error);
  }
  if (entriesResult.error) {
    console.error('Error fetching project entries stats:', entriesResult.error);
  }
  if (dataEntriesResult.error) {
    console.error('Error fetching project data entries stats:', dataEntriesResult.error);
  }
  if (baseFilesResult.error) {
    console.error('Error fetching project base files stats:', baseFilesResult.error);
  }

  const odysseyJobs = odysseyJobsResult.data || [];
  // Count completed Odyssey jobs as generations
  const completedOdysseyJobs = odysseyJobs.filter(j => j.status === 'completed');
  
  
  // Determine file types from combined result
  const baseFiles = baseFilesResult.data || [];
  const hasBasePdb = baseFiles.some(file => file.file_type === 'pdb');
  const hasBaseCif = baseFiles.some(file => file.file_type === 'cif');
  
  const stats = {
    totalJobs: odysseyJobs.length,
    completedJobs: completedOdysseyJobs.length,
    runningJobs: odysseyJobs.filter(j => j.status === 'running' || j.status === 'queued').length,
    failedJobs: odysseyJobs.filter(j => j.status === 'failed').length,
    totalEntries: entriesResult.data?.length || 0,
    totalDataEntries: dataEntriesResult.data?.length || 0,
    maxGeneration: completedOdysseyJobs.length, // Count of completed Odyssey pipeline runs
    hasBasePdb,
    hasBaseCif
  };

  return stats;
};

// Batch version to get stats for multiple projects efficiently
export const getProjectStatsBatch = async (projectIds: string[]): Promise<Record<string, {
  totalJobs: number;
  completedJobs: number;
  runningJobs: number;
  failedJobs: number;
  totalEntries: number;
  totalDataEntries: number;
  maxGeneration: number;
  hasBasePdb: boolean;
  hasBaseCif: boolean;
}>> => {
  if (projectIds.length === 0) return {};

  // Batch fetch all data for all projects in parallel
  const [odysseyJobsResult, entriesResult, dataEntriesResult, baseFilesResult] = await Promise.all([
    supabase
      .from('odyssey_jobs')
      .select('project_id, status')
      .in('project_id', projectIds),
    supabase
      .from('entries')
      .select('project_id')
      .in('project_id', projectIds),
    supabase
      .from('data_entries')
      .select('project_id')
      .in('project_id', projectIds),
    supabase
      .from('molecule_viewer')
      .select('project_id, file_type')
      .in('project_id', projectIds)
      .eq('isbasepdb', true)
      .in('file_type', ['pdb', 'cif'])
  ]);

  // Log errors but don't fail
  if (odysseyJobsResult.error) {
    console.error('Error fetching batch odyssey jobs:', odysseyJobsResult.error);
  }
  if (entriesResult.error) {
    console.error('Error fetching batch entries:', entriesResult.error);
  }
  if (dataEntriesResult.error) {
    console.error('Error fetching batch data entries:', dataEntriesResult.error);
  }
  if (baseFilesResult.error) {
    console.error('Error fetching batch base files:', baseFilesResult.error);
  }

  // Group data by project ID
  const odysseyJobsByProject = (odysseyJobsResult.data || []).reduce((acc, job) => {
    if (!acc[job.project_id]) acc[job.project_id] = [];
    acc[job.project_id].push(job);
    return acc;
  }, {} as Record<string, any[]>);

  const entriesByProject = (entriesResult.data || []).reduce((acc, entry) => {
    if (!acc[entry.project_id]) acc[entry.project_id] = 0;
    acc[entry.project_id]++;
    return acc;
  }, {} as Record<string, number>);

  const dataEntriesByProject = (dataEntriesResult.data || []).reduce((acc, entry) => {
    if (!acc[entry.project_id]) acc[entry.project_id] = 0;
    acc[entry.project_id]++;
    return acc;
  }, {} as Record<string, number>);

  const baseFilesByProject = (baseFilesResult.data || []).reduce((acc, file) => {
    if (!acc[file.project_id]) acc[file.project_id] = [];
    acc[file.project_id].push(file);
    return acc;
  }, {} as Record<string, any[]>);

  // Calculate stats for each project
  const result: Record<string, any> = {};
  
  for (const projectId of projectIds) {
    const odysseyJobs = odysseyJobsByProject[projectId] || [];
    const completedOdysseyJobs = odysseyJobs.filter(j => j.status === 'completed');
    const baseFiles = baseFilesByProject[projectId] || [];
    
    result[projectId] = {
      totalJobs: odysseyJobs.length,
      completedJobs: completedOdysseyJobs.length,
      runningJobs: odysseyJobs.filter(j => j.status === 'running' || j.status === 'queued').length,
      failedJobs: odysseyJobs.filter(j => j.status === 'failed').length,
      totalEntries: entriesByProject[projectId] || 0,
      totalDataEntries: dataEntriesByProject[projectId] || 0,
      maxGeneration: completedOdysseyJobs.length,
      hasBasePdb: baseFiles.some(file => file.file_type === 'pdb'),
      hasBaseCif: baseFiles.some(file => file.file_type === 'cif')
    };
  }

  return result;
};

// Month-based calendar functions for performance optimization
const getMonthRange = (year: number, month: number) => {
  const startDate = new Date(year, month, 1);
  const endDate = new Date(year, month + 1, 0, 23, 59, 59, 999);
  return {
    start: startDate.toISOString(),
    end: endDate.toISOString()
  };
};

// Get projects for a specific month
export const getProjectsForMonth = async (year: number, month: number) => {
  const { start, end } = getMonthRange(year, month);
  
  const { data: projects, error } = await supabase
    .from('projects')
    .select('id, name, created_at, workflow_type')
    .eq('user_id', await getCurrentUserId())
    .gte('created_at', start)
    .lte('created_at', end)
    .order('created_at', { ascending: false });

  if (error) {
    console.error('Error fetching projects for month:', error);
    throw new Error(`Failed to fetch projects: ${error.message}`);
  }

  return projects || [];
};